import { autoinject, BindingEngine, computedFrom, customElement, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { Router } from 'aurelia-router';
import { HOME_ROUTE } from 'support/settings';
import { ValidationRules } from 'aurelia-validation';
import { CountryDetailData } from 'model/forms/country-detail-data';
import { EmailAddressWrapper, SystemUserContactWrapper, SystemUserWrapper } from 'model/support-types';
import { KeywordsService } from 'services/keywords-service';
import { ModelBase } from 'support/base_classes/model-base';
import { EnumsService } from 'services/enums-service';
import { WidgetButton } from 'controls/as-widget-stack';
import { CountryApprovalRequirement } from 'api/swagger/ApiClient';

@autoinject
@customElement('country-form')
export class CountryFormCustomElement extends ModelBase<CountryDetailData> {
  @bindable autofocus = false;
  @bindable disabled = false;
  newEmptyGenericEmailAddressWrapper: EmailAddressWrapper = { emailAddress: null };
  newEmptyApproverWrapper: SystemUserWrapper = { user: null, userId: null };
  newEmptyContactWrapper: SystemUserContactWrapper = { user: null, userId: null, countriesContactEmail: null };
  @bindable({ defaultBindingMode: bindingMode.fromView }) genericEmailAddressesEditing = false;
  @bindable({ defaultBindingMode: bindingMode.fromView }) approversEditing = false;
  @bindable({ defaultBindingMode: bindingMode.fromView }) contactsEditing = false;
  approverButtons: WidgetButton[] = [{
    icon: "fa-user-tag",
    title: "Manage Countries Roles",
    onClick: (item: SystemUserWrapper) => {
      this.router.navigate(`#/${HOME_ROUTE}/admin/countriesRoles/${item.user.uniqueid}`);
    },
  }];
  contactButtons: WidgetButton[] = [{
    icon: "fa-user-tag",
    title: "Manage Countries Roles",
    onClick: (item: SystemUserContactWrapper) => {
      this.router.navigate(`#/${HOME_ROUTE}/admin/countriesRoles/${item.user.uniqueid}`);
    },
  }];

  constructor(private router: Router, bindingEngine: BindingEngine, private keywords: KeywordsService, private enumService: EnumsService) {
    super(bindingEngine);
  }

  modelChanged(newValue: CountryDetailData) {
    super.modelChanged(newValue);
    this.newEmptyGenericEmailAddressWrapper = { emailAddress: null } as EmailAddressWrapper;
    this.newEmptyApproverWrapper = { user: null, userId: null } as SystemUserWrapper;
    this.newEmptyContactWrapper = { user: null, userId: null, countriesContactEmail: null } as SystemUserContactWrapper;

    ValidationRules
      .ensureObject<CountryDetailData>().required()
      .ensure(model => model.name).displayName('Country Name').required()
      .ensure(model => model.isoCode2).displayName('Alpha 2 ISO Code').required()
      .ensure(model => model.isoCode3).displayName('Alpha 3 ISO Code').required()
      .ensure(model => model.approvalLevel).displayName('Approval Level').required()
      .ensure(model => model.approvalRequirement).displayName('Approval').required()
      .ensure(model => model.approveType).displayName('Approval Category').required()
      .ensure(model => model.specificCondition).satisfies((specificCondition: string | null, country: CountryDetailData) => !country.hasSpecificCondition || specificCondition != null).withMessage('Specific Condition is required.')
      .on(this.model);
  }

  keywordsItemMatcher = this.keywords.itemsMatch;
  enumsItemMatcher = this.enumService.itemsMatch;

  @computedFrom('disabled', 'model.approvalRequirement')
  get approvalDisabled() {
    return this.disabled || this.model.approvalRequirement == null || this.model.approvalRequirement.value === CountryApprovalRequirement.NotRequired;
  }

  @computedFrom('disabled', 'model.approvalRequirement', 'model.hasSpecificCondition')
  get specificConditiondDisabled() {
    return this.approvalDisabled || !this.model.hasSpecificCondition;
  }

  @computedFrom('keywords.approvalLevels', 'model.approvalLevel')
  get approvalLevels() {
    return this.keywords.ensureItem(this.keywords.approvalLevels, this.model.approvalLevel);
  }

  redirectToQuestions() {
    this.router.navigate(`#/${HOME_ROUTE}/admin/questions`);
  }
}
